/* import __COLOCATED_TEMPLATE__ from './preview-module.hbs'; */
/* RESPONSIBLE TEAM: team-actions */
import type AppService from 'embercom/services/app-service';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { restartableTask, task } from 'ember-concurrency-decorators';
import type Conversation from 'embercom/objects/inbox/conversation';
import generateUUID from 'embercom/lib/uuid-generator';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type ContentImportService from 'embercom/services/content-import-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import {
  BULK_ACTION_PROGRESSION_ID,
  type BulkActionProgressionEvent,
  BulkActionProgressionValues,
} from 'embercom/services/knowledge-hub-service';
import ENV from 'embercom/config/environment';
import type FinOptInService from 'embercom/services/fin-opt-in-service';
import type { Brand } from 'embercom/components/new-settings/workspace/brands/types/brand';
import type BrandService from 'embercom/services/brand-service';
import type IntlService from 'ember-intl/services/intl';
import { localCopy } from 'tracked-toolbox';
import { post } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';
import type ThemeService from 'embercom/services/theme-service';
import { TrackedArray } from 'tracked-built-ins';
import type { PreviewRenderMode } from 'embercom/components/workflows/preview/layout-wrapper';
import type ProcedurePreviewService from 'embercom/services/procedure-preview-service';
import {
  PREVIEW_RESTART_EVENT,
  type PreviewTab,
} from 'embercom/services/procedure-preview-service';

interface Args {
  onClose?: () => void;
  messengerUrl: string;
  previewRefreshing?: boolean;
  onPreviewLoaded?: () => void;
  showChannelSelector?: boolean;
  skipHasContentReadyForFinCheck?: boolean;
  fixedChannel?: 'email' | 'chat';
  previewLocale?: string;
  onPreviewLocaleChange?: (locale: string) => void;
  disableAutoLanguageDetection?: boolean;
  headerTitle?: string;
  shouldCreateConversation?: boolean;
  previewWorkflowInstanceId?: string;
  showAudiences?: boolean;
  hideImpersonationControls?: boolean;
  layout?: PreviewRenderMode;
  showPreviewActions: boolean;
  isOnProceduresPage: boolean;
}

interface Signature {
  Args: Args;
  Blocks: {
    default: [];
    additionalHeaderActions: any;
  };
  Element: HTMLElement;
}

export const CONVERSATION_EVENTS = [
  'ThreadAssigned',
  'ThreadReopened',
  'ThreadCreated',
  'ThreadClosed',
  'ThreadUpdated',
  'ThreadSnoozed',
  'ThreadUnsnoozed',
  'ConversationAttributesUpdated',
  'ConversationPartUpdated',
  'TicketStateUpdated',
];

export type PreviewSpoofingMessengerWireSettings = {
  userId: string | null;
  brandId: string | null;
  shouldCallRealActions: boolean | null;
  asNewLead: boolean;
  selectedContentSegmentIds: string[] | null;
};

export type PreviewSpoofingIntercomWireSettings =
  KeysToSnakeCase<PreviewSpoofingMessengerWireSettings>;

export type PreviewSpoofingSettings = {
  user: UserSummary | null;
  brandId: string | null;
  shouldCallRealActions?: boolean | null;
  asNewLead?: boolean;
  selectedContentSegmentIds: TrackedArray<string>;
};

export const PREVIEW_CONVERSATION_STARTED_EVENT_NAME = 'PreviewConversationStarted';

export const MAX_USERNAME_LENGTH = 22;

type PreviewSettingsModalMode = 'settings' | 'search' | 'audience';

export default class PreviewModule extends Component<Signature> {
  @service declare session: Session;
  @service declare themeService: ThemeService;
  @service declare procedurePreviewService: ProcedurePreviewService;

  @tracked conversationId: number | undefined;
  @tracked conversation: Conversation | undefined;
  @tracked _selectedSpoofUser: UserSummary | null = null;
  @tracked _selectedSpoofBrand: Brand | null = null;
  @tracked _shouldCallRealActions = false;
  @tracked _asNewLead = false;
  @tracked _selectedSpoofAudiences: TrackedArray<string> | null = new TrackedArray<string>([]);
  @tracked previewChannel: 'email' | 'chat' = this.args.fixedChannel ?? 'chat';
  @tracked isPreviewSettingsModalOpen = false;
  @tracked previewSettingsModalInSearchMode = false;
  @tracked previewSettingsModalInAudienceMode = false;
  @tracked isPreviewAsHovered = false;
  @tracked pendingSpoofingSettingsUpdate = false;
  @localCopy('args.previewLocale') declare previewLocale: string;

  previewSessionId: string;

  // these are used to track the settings and filters count for instrumentation
  _filtersCount = 0;

  @service declare realTimeEventService: any;
  @service declare inboxApi: any;
  @service declare contentImportService: ContentImportService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare finOptInService: FinOptInService;
  @service declare intercomEventService: $TSFixMe;
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare brandService: BrandService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    if (!this.args.skipHasContentReadyForFinCheck) {
      this.contentImportService.subscribeToContentImportRunStatusUpdates();
      this.knowledgeHubService.subscribeToSourceSyncEvents();
      this.contentImportService.fetchContentImportSources();
      this.contentImportService.fetchContentIngestionState();
      this.aiAgentSetupService.loadKnowledgeUsageSummary();
      this.setBulkActionProgressActions();
    }

    this.previewSessionId = generateUUID();
    this.realTimeEventService.on(
      PREVIEW_CONVERSATION_STARTED_EVENT_NAME,
      this,
      'handlePreviewConversationStarted',
    );
    CONVERSATION_EVENTS.forEach((event) => {
      this.realTimeEventService.on(event, this, 'handleAdditionalConversationEvents');
    });
    this.procedurePreviewService.on(PREVIEW_RESTART_EVENT, this, 'handlePreviewRestart');
  }

  get previewIsLoading() {
    return this.args.previewRefreshing || this.pendingSpoofingSettingsUpdate;
  }

  @action
  onPreviewLoaded() {
    this.args.onPreviewLoaded?.();

    // in previews where a new conversation is not created on load, we need to clear the event log
    if (!this.args.shouldCreateConversation) {
      this.clearEventLog();
    }

    this.procedurePreviewService.selectedTab = 'customer';

    if (this.pendingSpoofingSettingsUpdate) {
      taskFor(this.sendSpoofingSettingsToMessenger).perform();
    }
  }

  get isFirefoxOrSafari() {
    let userAgent = navigator.userAgent.toLowerCase();
    return /firefox|safari/i.test(userAgent) && !/chrome/i.test(userAgent);
  }

  @action
  onMessage(data: Record<string, any>) {
    if (data.type === 'intercom-messenger-visible') {
      // https://github.com/intercom/intercom/issues/388796
      if (this.isFirefoxOrSafari) {
        this.restartPreview();
      }
    }
  }

  willDestroy() {
    super.willDestroy();
    this.realTimeEventService.off(
      PREVIEW_CONVERSATION_STARTED_EVENT_NAME,
      this,
      'handlePreviewConversationStarted',
    );
    this.destroyBulkActionProgressActions();
    this.knowledgeHubService.unsubscribeToSourceSyncEvents();
    CONVERSATION_EVENTS.forEach((event) => {
      this.realTimeEventService.off(event, this, 'handleAdditionalConversationEvents');
    });
    this.procedurePreviewService.off(PREVIEW_RESTART_EVENT, this, 'handlePreviewRestart');
  }

  get messengerUrl() {
    let iframeUrl = `${this.args.messengerUrl}&preview_session_id=${this.previewSessionId}`;

    if (this.themeService.darkModeEnabled) {
      iframeUrl += `&theme_mode=dark`;
    }

    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      if (this.previewLocale !== 'auto' && this.previewLocale) {
        iframeUrl += `&language_override=${this.previewLocale}`;
      }
    } else if (this.previewLocale !== 'auto' && this.previewLocale) {
      iframeUrl += `&language_override=${this.previewLocale}`;
    } else {
      iframeUrl += `&language_override=en`;
    }

    return iframeUrl;
  }

  get hasContentReadyForFin() {
    return (
      this.aiAgentSetupService.hasContentReadyForFin &&
      this.contentImportService?.finIngestionState?.hasIngestedContent
    );
  }

  get hasNoContentAndIsIngesting() {
    return !this.hasContentReadyForFin && this.contentImportService?.inProgressRunExists;
  }

  get showImpersonationControls() {
    return !this.args.hideImpersonationControls;
  }

  get canUseImpersonation(): boolean {
    return (
      this.appService.app.canUseFinPreviewImpersonation &&
      this.permissionsService.currentAdminCan('can_access_contacts') &&
      this.permissionsService.currentAdminCan('can_access_user_profiles')
    );
  }

  get previewAsColor() {
    return this.isPreviewAsHovered ? 'text-accent' : 'text-default';
  }

  get canCreatePreviewConversation() {
    return (
      this.args.shouldCreateConversation &&
      this.args.previewWorkflowInstanceId &&
      (this.selectedSpoofUser || this.asNewLead)
    );
  }

  @action
  handlePreviewConversationStarted(event: any) {
    let eventConversationId = Number(event.conversationId);
    let eventPreviewSessionId = event.previewSessionId;

    // if the preview conversation is from a different session, we don't want to fetch the conversation
    if (eventPreviewSessionId !== this.previewSessionId) {
      return;
    }

    if (!eventConversationId) {
      return;
    }

    let nullImpersonationType = this.asNewLead ? 'new_lead' : 'preview_user';

    this.intercomEventService.trackAnalyticsEvent({
      action: 'started',
      object: 'preview_conversation',
      place: 'workflow_preview',
      section: 'preview',
      impersonation: this.selectedSpoofUser ? 'real_user' : nullImpersonationType,
      user_role: this.selectedSpoofUser?.role,
      language: this.previewLocale !== 'auto' ? 'chose_language' : 'auto_detect',
      brand: this.currentBrandId ? 'chose_brand' : 'default_brand',
      action_type: this._shouldCallRealActions ? 'live_data' : 'sample_data',
      as_new_lead: this._asNewLead,
      filters_count: this._filtersCount,
    });

    this.conversationId = eventConversationId;
    this.procedurePreviewService.conversationId = eventConversationId;
    taskFor(this.fetchConversation).perform();
    console.debug('PREVIEW CONVERSATION ID:', this.conversationId);
  }

  @action
  handleAdditionalConversationEvents(event: any) {
    let eventConversationId = Number(event.conversationId);

    if (eventConversationId !== this.conversationId) {
      return;
    }

    // For now let's just re-fetch the conversation when we get any of these events
    taskFor(this.fetchConversation).perform();
  }

  @task({ drop: true })
  *fetchConversation(): TaskGenerator<void> {
    if (!this.conversationId) {
      return;
    }

    let conversation = yield this.inboxApi.fetchConversation(this.conversationId);
    this.conversation = conversation;
    this.procedurePreviewService.conversation = conversation;
    if (this.appService.app.canUseMBFAiAgentIdentities) {
      this.session.workspace.fetchFeatures();
    }
  }

  get isFetchingConversation() {
    return taskFor(this.fetchConversation).isRunning;
  }

  get selectedSpoofUserName() {
    if (this.selectedSpoofUser?.displayAs) {
      return this.selectedSpoofUser?.displayAs;
    }

    if (this.asNewLead) {
      return this.intl.t('operator.custom-bot.editor.preview.new-lead');
    }

    return this.intl.t('operator.custom-bot.editor.preview.preview-user');
  }

  @action
  restartPreview() {
    this.procedurePreviewService.restartPreview();
  }

  handlePreviewRestart() {
    this.clearEventLog();
    this.procedurePreviewService.selectedTab = 'customer';
  }

  get selectedTab(): PreviewTab {
    return this.procedurePreviewService.selectedTab;
  }

  @action
  setSelectedSpoofUser(user: UserSummary | null) {
    this._selectedSpoofUser = user;
    if (user != null) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'selected',
        object: 'preview_impersonation_user',
      });
    }
  }

  @action
  setSelectedSpoofAudiences(audiences: TrackedArray<string> | null) {
    this._selectedSpoofAudiences = audiences;
  }

  clearEventLog() {
    this.conversation = undefined;
    this.conversationId = undefined;
    this.procedurePreviewService.conversation = undefined;
    this.procedurePreviewService.conversationId = undefined;
    taskFor(this.fetchConversation).cancelAll();
  }

  setSelectedSpoofBrand(brandId: string | null) {
    if (brandId) {
      let foundBrand = this.brandService.brands.find((brand) => brand.id.toString() === brandId);
      if (foundBrand) {
        this._selectedSpoofBrand = foundBrand as unknown as Brand;
      }
    } else {
      this._selectedSpoofBrand = null;
    }
  }

  setShouldCallRealActions(shouldCallRealActions: boolean) {
    this._shouldCallRealActions = shouldCallRealActions;
  }

  setAsNewLead(asNewLead: boolean) {
    this._asNewLead = asNewLead;
  }

  @action
  openPreviewSettingsModal(mode: PreviewSettingsModalMode = 'settings') {
    this.previewSettingsModalInSearchMode = mode === 'search';
    this.previewSettingsModalInAudienceMode = mode === 'audience';
    this.isPreviewSettingsModalOpen = true;
  }

  @action
  closePreviewSettingsModal() {
    this.previewSettingsModalInSearchMode = false;
    this.isPreviewSettingsModalOpen = false;
  }

  @action
  handlePreviewSettingsSave(settings: PreviewSpoofingSettings, locale?: string, filtersCount = 0) {
    let localeChanged = locale && locale !== this.previewLocale;

    this.setSelectedSpoofBrand(settings.brandId);
    this.setSelectedSpoofUser(settings.user);
    this.setShouldCallRealActions(settings.shouldCallRealActions ?? false);
    this.setAsNewLead(settings.asNewLead ?? false);
    this.setSelectedSpoofAudiences(settings.selectedContentSegmentIds);
    this._filtersCount = filtersCount;

    if (localeChanged && locale) {
      this.pendingSpoofingSettingsUpdate = true;

      if (this.args.onPreviewLocaleChange) {
        // Spoofing settings are applied after the preview reloads
        this.args.onPreviewLocaleChange?.(locale);
      } else {
        this.previewLocale = locale;
      }
    } else {
      taskFor(this.sendSpoofingSettingsToMessenger).perform();
    }

    this.clearEventLog();
    this.procedurePreviewService.selectedTab = 'customer';
  }

  @restartableTask
  *sendSpoofingSettingsToMessenger(): TaskGenerator<void> {
    let settings: PreviewSpoofingMessengerWireSettings = {
      shouldCallRealActions: this.appService.app.canUseFinPreviewImpersonation
        ? this._shouldCallRealActions
        : null,
      userId: this._selectedSpoofUser?.id?.toString() ?? null,
      brandId: this._selectedSpoofBrand?.id?.toString() ?? null,
      asNewLead: this._asNewLead,
      selectedContentSegmentIds: this._selectedSpoofAudiences ?? null,
    };
    let iframe = window.document.querySelector(
      '#hosted-messenger-unified-preview',
    ) as HTMLIFrameElement;

    let conversationId;

    if (this.canCreatePreviewConversation) {
      conversationId = yield taskFor(this.createInitialWorkflowPreviewConversation).perform(
        settings,
      );
    } else {
      yield;
    }

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify({
          type: 'preview-settings-updated',
          previewSpoofingSettings: settings,
          conversationId,
        }),
      );
    }

    this.pendingSpoofingSettingsUpdate = false;
  }

  // Used for Outbound, Reusable, and Button workflows, which create a new conversation when loading the preview
  // this is done when rendering the iframe for the first time
  // when spoofing, we don't re-render the iframe, we only send a message with updated settings, so we need to do the conversation creation job ourselves
  // there might be a way of moving the job to the messenger, but it might be too complex to implement
  @restartableTask
  *createInitialWorkflowPreviewConversation(
    spoofingSettings: PreviewSpoofingMessengerWireSettings,
  ): TaskGenerator<string> {
    try {
      let url = `${window.location.origin}/hosted_messenger/${this.appService.app.id}/create_workflow_preview_conversation`;

      let intercomSpoofingSettings: PreviewSpoofingIntercomWireSettings = {
        should_call_real_actions: spoofingSettings.shouldCallRealActions,
        user_id: spoofingSettings.userId,
        brand_id: spoofingSettings.brandId,
        as_new_lead: spoofingSettings.asNewLead,
        selected_content_segment_ids: spoofingSettings.selectedContentSegmentIds?.toArray() ?? null,
      };

      let response = yield post(url, {
        preview_spoofing_settings: intercomSpoofingSettings,
        preview_session_id: this.previewSessionId,
        preview_workflow_instance_id: this.args.previewWorkflowInstanceId,
      });

      return response.conversation_id;
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('operator.custom-bot.editor.preview.preview-settings.error'),
      );
      throw new Error('Failed to create preview conversation');
    }
  }

  // the component requires undefined rather than null
  get selectedSpoofUser(): UserSummary | undefined {
    return this._selectedSpoofUser ?? undefined;
  }

  get selectedSpoofAudiences(): TrackedArray<string> | undefined {
    return this._selectedSpoofAudiences ?? undefined;
  }

  get currentBrandId(): string | null {
    return this._selectedSpoofBrand?.id?.toString() ?? null;
  }

  get currentUserId(): string | null {
    return this._selectedSpoofUser?.id?.toString() ?? null;
  }

  get shouldCallRealActions(): boolean {
    return this._shouldCallRealActions;
  }

  get asNewLead(): boolean {
    return this._asNewLead;
  }

  get previewSettings(): PreviewSpoofingSettings {
    return {
      user: this._selectedSpoofUser,
      brandId: this._selectedSpoofBrand?.id?.toString() ?? null,
      shouldCallRealActions: this._shouldCallRealActions,
      asNewLead: this._asNewLead,
      selectedContentSegmentIds: this._selectedSpoofAudiences ?? new TrackedArray([]),
    };
  }

  private setBulkActionProgressActions() {
    this.realTimeEventService.on(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  private destroyBulkActionProgressActions() {
    this.realTimeEventService.off(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  async handleBulkActionProgression(event: BulkActionProgressionEvent) {
    if (event.status === BulkActionProgressionValues.Complete) {
      if (!event.all_entities_unaffected) {
        setTimeout(() => {
          this.knowledgeHubService.fetchKnowledgeUsageSummary();
        }, ENV.APP._500MS);
      }
    }
  }

  @action
  togglePreviewChannel() {
    this.previewChannel = this.previewChannel === 'email' ? 'chat' : 'email';
  }

  @task({ drop: true })
  *consentToExternalAi() {
    yield taskFor(this.finOptInService.consentToExternalAi).perform();
  }

  get app() {
    return this.appService.app;
  }

  get isFinPreview() {
    return this.messengerUrl.includes('fin_demo_preview');
  }

  get showPreviewControls() {
    if (!this.app.hasConsentedToExternalAi && this.isFinPreview) {
      return false;
    }

    if (this.app.isTestApp && this.isFinPreview) {
      return false;
    }

    return this.hasContentReadyForFin || this.args.skipHasContentReadyForFinCheck;
  }

  @action
  onTabChange(tab: string) {
    this.procedurePreviewService.selectedTab = tab as PreviewTab;

    if (tab === 'teammate') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'preview_teammate_tab',
        place: 'workflow_preview',
        section: 'preview',
      });
    }
  }

  @action
  trackOpenPreviewSettingsModalButton(button: 'view_as_x_button' | 'preview_settings_button') {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: button,
      place: 'workflow_preview',
      section: 'preview',
    });
  }

  @action
  handleOpenUserSearch() {
    // Track analytics before opening modal
    this.trackOpenPreviewSettingsModalButton('view_as_x_button');
    // Open the settings modal directly in search mode
    this.openPreviewSettingsModal('search');
  }

  @action
  handleOpenAudienceSearch() {
    this.openPreviewSettingsModal('audience');
  }

  @action
  handleSelectPreviewUser() {
    // Clear user selection and switch to preview user mode
    this.setSelectedSpoofUser(null);
    this.setAsNewLead(false);
    this.setSelectedSpoofBrand(null);
    this.setSelectedSpoofAudiences(new TrackedArray([]));
    taskFor(this.sendSpoofingSettingsToMessenger).perform();
    this.clearEventLog();
    this.procedurePreviewService.selectedTab = 'customer';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::PreviewModule': typeof PreviewModule;
    'workflows/preview/preview-module': typeof PreviewModule;
  }
}
